import { useEffect, useId, useMemo, useState } from 'react';

import { computeFullViewBox } from '~utils';
import { useGridSpacing } from '../hooks/useGridSpacing';
import { useSvgContext } from './SvgContext';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type SvgGridProps = {
  /**
   * Desired number of grid lines visible on screen at any zoom level.
   * The actual spacing adapts as the user zooms in/out, always snapping
   * to a "nice" round value (1, 2, 5 × 10^n).
   * @default 20
   */
  targetLines?: number;
  /**
   * Grid line stroke width in screen pixels. Uses `non-scaling-stroke` so
   * lines maintain consistent visual weight regardless of zoom level.
   * @default 1.5
   */
  strokeWidth?: number;
  /** Grid line color @default 'var(--color-border)' */
  color?: string;
  /** Grid line opacity @default 0.4 */
  opacity?: number;
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const DEFAULT_STROKE_WIDTH = 1.5;
const DEFAULT_COLOR = 'var(--color-border)';
const DEFAULT_OPACITY = 0.4;

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

/**
 * Sanitize a React `useId()` value for use in SVG `url(#...)` references.
 * React's `useId` returns IDs containing colons (e.g. `:r1:`) which are invalid
 * in unescaped CSS selectors and can cause issues in SVG `url()` references.
 */
const sanitizeId = (id: string): string => id.replace(/:/g, '_');

/* -------------------------------------------------------------------------- */
/*                               Sub-components                               */
/* -------------------------------------------------------------------------- */

/**
 * SVG `<defs>` with a grid line pattern.
 *
 * Grid lines use `vector-effect="non-scaling-stroke"` so they maintain a
 * consistent screen-pixel width regardless of the current zoom level.
 * The pattern spacing is in SVG coordinate units (local coords ≈ meters).
 */
const GridPattern = ({
  patternId,
  spacing,
  strokeWidth,
  color,
  opacity,
}: {
  patternId: string;
  spacing: number;
  strokeWidth: number;
  color: string;
  opacity: number;
}) => (
  <defs>
    <pattern id={patternId} x={0} y={0} width={spacing} height={spacing} patternUnits="userSpaceOnUse">
      <line
        x1={0}
        y1={spacing}
        x2={spacing}
        y2={spacing}
        stroke={color}
        strokeWidth={strokeWidth}
        opacity={opacity}
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={spacing}
        y1={0}
        x2={spacing}
        y2={spacing}
        stroke={color}
        strokeWidth={strokeWidth}
        opacity={opacity}
        vectorEffect="non-scaling-stroke"
      />
    </pattern>
  </defs>
);

/**
 * Background rect filled with the grid pattern, positioned in the **transformed SVG
 * coordinate space** (where content is rendered).
 *
 * The rect is computed to precisely cover the currently visible viewport area in local
 * coordinates, so the grid remains fully visible at any zoom or pan position. A one-tile
 * buffer is added on each side to prevent gaps at pattern tile boundaries.
 */
const GridBackground = ({
  patternId,
  x,
  y,
  width,
  height,
}: {
  patternId: string;
  x: number;
  y: number;
  width: number;
  height: number;
}) => (
  // biome-ignore lint/a11y/noAriaHiddenOnFocusable: This rect is purely decorative and should not receive pointer events or accessibility focus */}
  <rect
    x={x}
    y={y}
    width={width}
    height={height}
    fill={`url(#${patternId})`}
    aria-hidden="true"
    className="pointer-events-none"
  />
);

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Adaptive grid background for `SvgCanvas`.
 *
 * Renders a grid pattern that adapts to the current zoom level — as the user
 * zooms in, lines subdivide to maintain a consistent visual density; as they
 * zoom out, lines consolidate. Spacing always snaps to "nice" values
 * (1, 2, 5 × 10^n) so each grid cell represents a measurable real-world
 * distance (e.g., 50m, 20m, 10m).
 *
 * Reads bounds and zoom transform from `SvgContext` — no manual wiring needed.
 * Place as a child of `<SvgCanvas>` **before** other content so the grid
 * renders behind all visual elements.
 *
 * @example
 * ```tsx
 * <SvgCanvas boundedByGeometries={geometries} title="Blast visualization">
 *   <SvgGrid />
 *   <DrillHolesLayer holes={drillHoles} />
 * </SvgCanvas>
 *
 * // Custom density and styling
 * <SvgCanvas boundedByGeometries={geometries}>
 *   <SvgGrid targetLines={30} strokeWidth={1} opacity={0.2} />
 *   <DrillHolesLayer holes={drillHoles} />
 * </SvgCanvas>
 * ```
 */
const SvgGrid = ({
  targetLines,
  strokeWidth = DEFAULT_STROKE_WIDTH,
  color = DEFAULT_COLOR,
  opacity = DEFAULT_OPACITY,
}: SvgGridProps) => {
  const { viewBox, transform, svg } = useSvgContext();
  const { spacing } = useGridSpacing({ targetLines });
  const patternId = sanitizeId(useId());

  const [svgSize, setSvgSize] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (!svg) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const { width, height } = entry.contentRect;
        setSvgSize({ width, height });
      }
    });
    observer.observe(svg);
    return () => observer.disconnect();
  }, [svg]);

  /**
   * Expanded viewBox covering the full SVG element viewport, including the padding areas
   * introduced by `preserveAspectRatio="xMidYMid meet"` (letterbox/pillarbox).
   * Recomputes only when the viewBox string or the SVG element dimensions change.
   */
  const fullViewBox = useMemo(
    () => (viewBox ? computeFullViewBox(viewBox, svgSize?.width ?? 0, svgSize?.height ?? 0) : null),
    [viewBox, svgSize],
  );

  /**
   * Grid rect in local coordinates to cover the visible viewport at the current zoom/pan.
   *
   * The D3 zoom transform `(k, x, y)` maps a local point `(lx, ly)` to SVG coordinate
   * `(x + lx*k, y + ly*k)`. Inverting gives the visible local range. A one-tile buffer
   * is added on each side to prevent gaps at pattern tile boundaries.
   */
  const gridRect = useMemo(() => {
    if (!fullViewBox) return null;

    const { vx, vy, vw, vh } = fullViewBox;
    const { k, x, y } = transform;
    const buffer = spacing || 1;

    return {
      x: (vx - x) / k - buffer,
      y: (vy - y) / k - buffer,
      width: vw / k + 2 * buffer,
      height: vh / k + 2 * buffer,
    };
  }, [fullViewBox, transform, spacing]);

  if (spacing === 0 || !gridRect) return null;

  return (
    <>
      <GridPattern patternId={patternId} spacing={spacing} strokeWidth={strokeWidth} color={color} opacity={opacity} />
      <GridBackground patternId={patternId} {...gridRect} />
    </>
  );
};
SvgGrid.displayName = 'SvgGrid';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export type { SvgGridProps };
export { SvgGrid };
